import { cssJoin } from '@volvo-cars/css/utils';
import React, { CSSProperties, useState } from 'react';

import styles from './TabContainer.module.css';

export type TabContainerProps = {
  title?: string;
  defaultTabKey?: number;
  contentBg?: 'primary' | 'secondary';
  headerBg?: 'primary' | 'secondary';
  wideHeaderItem?: boolean;
  children: React.ReactElement<TabProps>[];
};

export type TabProps = {
  title: string;
  paddedContent?: boolean;
  children: React.ReactNode;
};

type TabHeaderProps = Pick<TabContainerProps, 'title' | 'headerBg'> & {
  children: React.ReactNode;
};

type TabHeaderItemType = {
  title: string;
  tabKey: number;
};

const TabContainer: React.FC<TabContainerProps> = ({
  title,
  defaultTabKey = 1,
  contentBg = 'secondary',
  headerBg = 'secondary',
  wideHeaderItem = false,
  children,
}) => {
  const [activeTab, setActiveTab] = useState<number>(defaultTabKey - 1);
  const tabHeaderItems = getTabHeaderItems(children);

  return (
    <>
      <TabHeader title={title} headerBg={headerBg}>
        {tabHeaderItems.map(({ title, tabKey }) => (
          <TabHeaderItem
            key={tabKey}
            title={title}
            isActive={tabKey === activeTab}
            onClick={() => setActiveTab(tabKey)}
            wideHeaderItem={wideHeaderItem}
          />
        ))}
      </TabHeader>
      <div
        className={styles.content}
        style={
          {
            '--bg-color': `var(--v-color-background-${contentBg})`,
          } as CSSProperties
        }
      >
        {children[activeTab]}
      </div>
    </>
  );
};

export const Tab: React.FC<TabProps> = ({ children, paddedContent }) => (
  <div className={cssJoin(paddedContent && styles.paddedContent)}>
    {children}
  </div>
);

const TabHeader: React.FC<TabHeaderProps> = ({ title, headerBg, children }) => (
  <div
    className={styles.header}
    style={
      {
        '--bg-color': `var(--v-color-background-${headerBg})`,
      } as CSSProperties
    }
  >
    {title && <p className={styles.title}> {title}</p>}
    <div className="flex-row">{children}</div>
  </div>
);

const TabHeaderItem: React.FC<{
  title: string;
  isActive: boolean;
  wideHeaderItem: boolean;
  onClick: () => void;
}> = ({ title, isActive, wideHeaderItem, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-active={isActive}
    className={cssJoin(
      styles.itemHeader,
      wideHeaderItem && styles.itemHeaderWide,
    )}
  >
    {capitalizeFirstLetter(title)}
  </button>
);

const getTabHeaderItems = (
  children: React.ReactElement<TabProps>[],
): TabHeaderItemType[] =>
  children.map((child, index) => {
    if (!child.props.title)
      console.error(
        'Please provide required "title" prop to the TabComponent child element.',
      );

    return {
      tabKey: index,
      title: child.props.title,
    };
  });

const capitalizeFirstLetter = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

export default TabContainer;
